(() => {
  if (customElements.get('strap-product')) return;

  class StrapProduct extends HTMLElement {
    connectedCallback() {
      try {
        this.straps = JSON.parse(this.querySelector('[data-strap-data]').textContent);
        this.labels = JSON.parse(this.querySelector('[data-strap-labels]').textContent);
      } catch (err) {
        console.error('[strap-product] invalid JSON data', err);
        return;
      }

      this.byId = new Map(this.straps.map((s) => [String(s.id), s]));
      this.order = this.straps.map((s) => String(s.id));
      this.qty = new Map();

      this.currentId = String(this.dataset.currentId);
      this.activeId = this.byId.has(this.currentId) ? this.currentId : this.order[0];

      const current = this.byId.get(this.activeId);
      if (current && current.available && current.max > 0) this.qty.set(this.activeId, 1);

      this.currency = this.dataset.currency || 'USD';
      this.locale = this.dataset.locale || document.documentElement.lang || 'en';

      this.form = document.getElementById(this.dataset.formId);
      if (this.form) this.form.setAttribute('data-hc-ignore', '');
      this.variantInput = this.form && this.form.querySelector('[data-variant-input]');
      this.qtyInput = this.querySelector('[data-qty-input]');
      this.card = this.querySelector('[data-card]');
      this.atc = this.querySelector('[data-atc]');
      this.errorEl = this.querySelector('[data-error]');

      this.onClick = this.onClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onQtyInput = this.onQtyInput.bind(this);

      this.addEventListener('click', this.onClick);
      if (this.form) this.form.addEventListener('submit', this.onSubmit);
      if (this.qtyInput) this.qtyInput.addEventListener('change', this.onQtyInput);

      this.querySelectorAll('[data-gallery]').forEach((g) => this.initGallery(g));
      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      if (this.qtyInput) this.qtyInput.removeEventListener('change', this.onQtyInput);
    }

    /* ───────── Events ───────── */

    onClick(e) {
      const swatch = e.target.closest('[data-swatch]');
      if (swatch) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        this.select(swatch.dataset.swatch);
        return;
      }

      const step = e.target.closest('[data-step]');
      if (step) {
        e.preventDefault();
        this.changeQty(this.activeId, Number(step.dataset.step));
        return;
      }

      const thumb = e.target.closest('[data-thumb]');
      if (thumb) {
        const gallery = thumb.closest('[data-gallery]');
        this.goToSlide(gallery, Number(thumb.dataset.thumb));
      }
    }

    onQtyInput() {
      const strap = this.byId.get(this.activeId);
      const n = parseInt(this.qtyInput.value, 10);
      this.setQty(this.activeId, Number.isFinite(n) ? n : 0, strap);
      this.render();
    }

    /* ───────── State ───────── */

    select(id) {
      id = String(id);
      const strap = this.byId.get(id);
      if (!strap) return;

      if (id === this.activeId) {
        if (!this.qty.get(id) && strap.available && strap.max > 0) {
          this.qty.set(id, 1);
          this.render();
        }
        return;
      }

      const anchor = this.querySelector(`[data-swatch="${id}"]`);
      const anchorTop = anchor ? anchor.getBoundingClientRect().top : null;

      this.activeId = id;
      this.switchGallery(id);

      if (this.variantInput) {
        this.variantInput.value = strap.variantId;
        this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      try {
        history.replaceState(history.state, '', strap.url);
      } catch (err) {
      }
      if (strap.pageTitle) document.title = strap.pageTitle;

      this.render();

      if (anchorTop !== null) {
        const shift = anchor.getBoundingClientRect().top - anchorTop;
        if (Math.abs(shift) > 1) window.scrollBy(0, shift);
      }

      this.dispatchEvent(
        new CustomEvent('strap:selected', {
          bubbles: true,
          detail: { productId: strap.id, variantId: strap.variantId, url: strap.url },
        })
      );
    }

    switchGallery(id) {
      let next = null;
      this.querySelectorAll('[data-gallery]').forEach((g) => {
        if (g.dataset.gallery === id) next = g;
        else g.hidden = true;
      });
      if (!next) return;
      next.hidden = false;
      const track = next.querySelector('[data-track]');
      if (track) track.scrollLeft = 0;
      this.setActiveThumb(next, 0);
    }

    setQty(id, value, strap = this.byId.get(id)) {
      const max = strap ? strap.max : 0;
      const n = Math.max(0, Math.min(max, value));
      if (n > 0) this.qty.set(id, n);
      else this.qty.delete(id);
      return n;
    }

    changeQty(id, delta) {
      this.setQty(id, (this.qty.get(id) || 0) + delta);
      this.hideError();
      this.render();
    }

    selectedItems() {
      return this.order
        .filter((id) => this.qty.get(id) > 0)
        .map((id) => ({ strap: this.byId.get(id), quantity: this.qty.get(id) }));
    }

    /* ───────── Render ───────── */

    render() {
      const active = this.byId.get(this.activeId);
      const activeQty = this.qty.get(this.activeId) || 0;

      this.querySelectorAll('[data-swatch]').forEach((el) => {
        const id = el.dataset.swatch;
        const isActive = id === this.activeId;
        el.classList.toggle('is-active', isActive);
        if (isActive) el.setAttribute('aria-current', 'true');
        else el.removeAttribute('aria-current');

        const badge = el.querySelector('[data-badge]');
        const q = this.qty.get(id) || 0;
        if (badge) {
          badge.hidden = q === 0;
          badge.textContent = q;
        }
      });

      if (active && this.card) {
        this.card.classList.toggle('is-selected', activeQty > 0);
        this.card.classList.toggle('is-soldout', !active.available);
        this.card.classList.toggle('is-preorder', active.available && active.preorder);

        const dot = this.card.querySelector('[data-card-dot]');
        dot.style.setProperty('--c', active.color);
        dot.style.setProperty('--img', active.swatchImage ? `url("${active.swatchImage}")` : 'none');

        this.card.querySelector('[data-card-name]').textContent = active.name;
        this.card.querySelector('[data-card-meta]').textContent =
          `${this.money(active.price)} · ${this.statusText(active)}`;

        const [minus, plus] = this.card.querySelectorAll('[data-step]');
        minus.disabled = activeQty <= 0;
        plus.disabled = !active.available || activeQty >= active.max;

        this.qtyInput.value = activeQty;
        this.qtyInput.max = active.max;
        this.qtyInput.disabled = !active.available;
      }

      const items = this.selectedItems();
      const count = items.reduce((n, i) => n + i.quantity, 0);
      const total = items.reduce((n, i) => n + i.quantity * i.strap.price, 0);

      this.querySelector('[data-summary]').textContent = items
        .map((i) => `${i.quantity} × ${i.strap.name}`)
        .join(' · ');
      this.querySelector('[data-total]').textContent = count ? this.money(total) : '';

      const note = this.querySelector('[data-preorder-note]');
      if (note) {
        const dates = [...new Set(items.filter((i) => i.strap.preorder).map((i) => i.strap.shipDate))];
        const show = dates.length > 0 && !!this.labels.preorderNote;
        note.hidden = !show;
        if (show) {
          note.querySelector('[data-preorder-note-text]').textContent = this.labels.preorderNote.replace(
            '[date]',
            dates.join(' / ')
          );
        }
      }

      const inst = this.querySelector('[data-installments]');
      if (inst) {
        const n = Math.max(1, Number(this.labels.installmentsCount) || 4);
        inst.hidden = total === 0;
        const amountEl = inst.querySelector('[data-installments-amount]');
        if (amountEl) amountEl.textContent = this.money(Math.ceil(total / n), true);
      }

      const label = this.atc.querySelector('[data-atc-label]');
      if (count === 0) {
        label.textContent = this.labels.buttonEmpty;
      } else {
        const tpl = count === 1 ? this.labels.buttonSingle : this.labels.buttonMulti;
        label.textContent = tpl.replace('[count]', count).replace('[total]', this.money(total));
      }
      this.atc.disabled = count === 0;
    }

    statusText(strap) {
      if (!strap.available) return this.labels.soldOut;
      if (strap.preorder) return this.labels.preorder.replace('[date]', strap.shipDate);
      return this.labels.ready;
    }

    money(cents, alwaysDecimals = false) {
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: alwaysDecimals || cents % 100 !== 0 ? 2 : 0,
          maximumFractionDigits: 2,
        }).format(cents / 100);
      } catch (err) {
        return (cents / 100).toFixed(alwaysDecimals || cents % 100 !== 0 ? 2 : 0);
      }
    }

    /* ───────── Gallery ───────── */

    initGallery(gallery) {
      const track = gallery.querySelector('[data-track]');
      const thumbs = gallery.querySelectorAll('[data-thumb]');
      if (!track || thumbs.length === 0) return;

      let raf = null;
      track.addEventListener(
        'scroll',
        () => {
          if (raf) return;
          raf = requestAnimationFrame(() => {
            raf = null;
            const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
            this.setActiveThumb(gallery, index);
          });
        },
        { passive: true }
      );

      track.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
        this.goToSlide(gallery, index + (e.key === 'ArrowRight' ? 1 : -1));
      });
    }

    goToSlide(gallery, index, behavior = 'smooth') {
      const track = gallery.querySelector('[data-track]');
      const slides = gallery.querySelectorAll('[data-slide]');
      if (!track || slides.length === 0) return;
      const i = Math.max(0, Math.min(slides.length - 1, index));
      track.scrollTo({ left: i * track.clientWidth, behavior });
      this.setActiveThumb(gallery, i);
    }

    setActiveThumb(gallery, index) {
      gallery.querySelectorAll('[data-thumb]').forEach((t) => {
        const on = Number(t.dataset.thumb) === index;
        t.classList.toggle('is-active', on);
        if (on) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
    }

    /* ───────── Cart ───────── */

    async onSubmit(e) {
      e.preventDefault();
      const items = this.selectedItems();
      if (items.length === 0) return;

      this.hideError();
      this.atc.classList.add('is-loading');
      this.atc.setAttribute('aria-busy', 'true');
      this.atc.disabled = true;

      const drawer = document.querySelector('cart-drawer');
      const sections =
        drawer && typeof drawer.getSectionsToRender === 'function'
          ? drawer.getSectionsToRender().map((s) => s.id)
          : [];

      const body = {
        items: items.map(({ strap, quantity }) => {
          const item = { id: strap.variantId, quantity };
          if (strap.preorder && this.labels.preorderProperty) {
            item.properties = {
              [this.labels.preorderProperty]: this.labels.preorderPropertyValue.replace('[date]', strap.shipDate),
            };
          }
          return item;
        }),
      };
      if (sections.length) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }

      const routes = window.routes || {};
      try {
        const res = await fetch(`${routes.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok || data.status) {
          throw new Error(data.description || data.message || this.labels.genericError);
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'strap-product',
            productVariantId: items[0].strap.variantId,
            cartData: data,
          });
        }

        this.dispatchEvent(
          new CustomEvent('strap:added', {
            bubbles: true,
            detail: { items: items.map((i) => ({ variantId: i.strap.variantId, quantity: i.quantity })) },
          })
        );

        if (window.__humeCartLoaded) {
        } else if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
          drawer.classList.remove('is-empty');
          drawer.renderContents(data);
        } else {
          window.location.href = '/checkout';
        }
      } catch (err) {
        this.showError(err.message || this.labels.genericError);
      } finally {
        this.atc.classList.remove('is-loading');
        this.atc.removeAttribute('aria-busy');
        this.render();
      }
    }

    showError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
    }

    hideError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }
  }

  customElements.define('strap-product', StrapProduct);
})();